import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plane, ArrowRight, Globe2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoading, EmptyState, fmtDate } from "@/components/shared";
import api from "@/lib/api";

const prettify = (s) => (s || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export default function ImmigrationCases() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [pathway, setPathway] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [pathways, setPathways] = useState([]);

  const load = () => {
    const p = new URLSearchParams();
    if (pathway !== "ALL") p.set("pathway", pathway);
    if (status !== "ALL") p.set("status", status);
    api.get(`/employer/immigration/cases?${p}`).then((r) => setRows(r.data.cases)).catch(() => setRows([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pathway, status]);
  useEffect(() => { api.get("/employer/immigration/rules").then((r) => setPathways(r.data.pathways)).catch(() => {}); }, []);

  return (
    <div data-testid="immigration-cases">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan/10 text-cyan-dark"><Plane className="h-5 w-5" /></span>
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl">{t("im.nav")}</h1>
          <p className="text-sm text-slate-500">{t("im.listSubtitle")}</p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={pathway} onValueChange={setPathway}>
          <SelectTrigger className="w-full sm:w-56" data-testid="imm-filter-pathway"><SelectValue placeholder={t("im.pathway")} /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">{t("im.allPathways")}</SelectItem>{pathways.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {rows === null ? <PageLoading /> : rows.length === 0 ? (
        <EmptyState icon={Globe2} title={t("im.noCases")} subtitle={t("im.noCasesHint")} />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="imm-cases-table">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="px-4 py-3">{t("im.worker")}</th><th className="px-4 py-3">{t("im.nationality")}</th><th className="px-4 py-3">{t("im.pathway")}</th><th className="px-4 py-3">{t("im.status")}</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50" onClick={() => nav(`/app/employer/immigration/${c.id}`)} data-testid={`imm-case-${c.id}`}>
                  <td className="px-4 py-3 font-semibold text-navy">{c.worker_name}</td>
                  <td className="px-4 py-3 text-slate-600">{c.nationality || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.pathway_label}</td>
                  <td className="px-4 py-3"><span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{prettify(c.status)}</span></td>
                  <td className="px-4 py-3 text-right"><ArrowRight className="ml-auto h-4 w-4 text-slate-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
